import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { clientServicesAPI, paymentsAPI, servicesAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { 
  Package, Calendar, AlertCircle, CheckCircle, CreditCard, XCircle, 
  Settings, RefreshCw, FileText, HelpCircle, ArrowUpCircle, Plus,
  Download, BookOpen, MessageSquare
} from 'lucide-react';
import { formatPrice, formatDate } from '../lib/utils';
import { toast } from 'sonner';

export default function MyServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [showRenewDialog, setShowRenewDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [relatedServices, setRelatedServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await clientServicesAPI.getMyServices();
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedServices = async (service) => {
    try {
      const response = await servicesAPI.getAll();
      // Filter to show services not currently owned
      const ownedServiceIds = services.map(s => s.service_id);
      const related = response.data
        .filter(s => !ownedServiceIds.includes(s.id) && s.is_active)
        .slice(0, 3);
      setRelatedServices(related);
    } catch (error) {
      console.error('Failed to load related services');
    }
  };

  const handleToggleAutoRenewal = async (serviceId, currentStatus) => {
    try {
      const response = await clientServicesAPI.toggleAutoRenewal(serviceId);
      toast.success(response.data.auto_renew ? 'Auto-renewal enabled' : 'Auto-renewal disabled');
      loadServices();
    } catch (error) {
      toast.error('Failed to update auto-renewal');
    }
  };

  const handleCancelService = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    setProcessing(true);
    try {
      await clientServicesAPI.cancelService(selectedService.id, cancellationReason);
      toast.success('Service cancelled. It will remain active until the end of the billing period.');
      setShowCancelDialog(false);
      setCancellationReason('');
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel service');
    } finally {
      setProcessing(false);
    }
  };

  const handleManagePayment = async () => {
    try {
      const returnUrl = window.location.origin + '/my-services';
      const response = await paymentsAPI.createCustomerPortal(returnUrl);
      
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      } else if (response.data.requires_configuration) {
        toast.info(response.data.message || 'To update your payment method, please contact support at support@edrano.my', {
          duration: 6000,
        });
      } else {
        toast.info(response.data.message || 'Payment management feature coming soon');
      }
    } catch (error) {
      toast.error('Failed to open payment management. Please contact support.');
    }
  };

  const handleOpenManageDialog = (service) => {
    setSelectedService(service);
    loadRelatedServices(service);
    setShowManageDialog(true);
  };

  const handleDownloadReceipt = (service) => {
    toast.success('Receipt download started');
    
    // Create receipt content
    const receiptContent = `
RECEIPT
=====================================
Edrano - Digital Services
support@edrano.my

Receipt Date: ${new Date().toLocaleDateString()}

Service: ${service.service_name}
Plan: ${service.pricing_tier_name}
Start Date: ${formatDate(service.start_date)}
End Date: ${formatDate(service.end_date)}

Amount Paid: ${formatPrice(service.price_paid)}

=====================================
Thank you for your business!

IMPORTANT NOTICE:
This is a payment receipt for your records only.
This document is NOT an official accounting document
and cannot be used for tax purposes.
For official invoices, please contact support@edrano.my
    `.trim();

    // Create downloadable file
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${service.service_name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'expired':
        return 'bg-red-100 text-red-700';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'expired':
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const isRenewingSoon = (endDate) => {
    const daysUntilRenewal = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntilRenewal <= 30 && daysUntilRenewal > 0;
  };

  const getDaysUntilRenewal = (endDate) => {
    return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  return (
    <TooltipProvider>
      <Layout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold mb-2">My Services</h1>
              <p className="text-muted-foreground">Manage your active subscriptions and services</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleManagePayment}>
                <CreditCard className="w-4 h-4 mr-2" />
                Payment Methods
              </Button>
              <Link to="/services">
                <Button data-testid="browse-services-button">
                  Browse More Services
                </Button>
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : services.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Active Services</h3>
                <p className="text-sm text-muted-foreground mb-4">You don&apos;t have any active services yet. Browse our catalog to get started!</p>
                <Link to="/services">
                  <Button>Browse Services</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {services.map((service) => (
                  <Card key={service.id} className="hover:shadow-lg transition-shadow" data-testid={`my-service-${service.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <CardTitle className="text-lg line-clamp-2 mb-2">{service.service_name}</CardTitle>
                          <CardDescription>{service.pricing_tier_name}</CardDescription>
                        </div>
                        <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                          {getStatusIcon(service.status)}
                          {service.status}
                        </span>
                      </div>
                      
                      {isRenewingSoon(service.end_date) && service.status === 'active' && (
                        <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <AlertCircle className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm text-yellow-800">Renews in {getDaysUntilRenewal(service.end_date)} days</span>
                        </div>
                      )}
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Price Paid</p>
                          <p className="font-semibold text-lg">{formatPrice(service.price_paid)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Start Date</p>
                          <p className="font-medium">{formatDate(service.start_date)}</p>
                        </div>
                      </div>

                      <div className="pt-3 border-t">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium">Renewal Date:</span>
                          </div>
                          <span className="font-semibold">{formatDate(service.end_date)}</span>
                        </div>

                        {service.status === 'active' && (
                          <div className="flex items-center justify-between py-2 px-3 bg-muted rounded-lg">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">Auto-renewal</span>
                              {service.auto_renew && (
                                <Badge variant="secondary" className="text-xs">Enabled</Badge>
                              )}
                            </div>
                            <Switch
                              checked={service.auto_renew || false}
                              onCheckedChange={() => handleToggleAutoRenewal(service.id, service.auto_renew)}
                            />
                          </div>
                        )}
                      </div>

                      {/* Action Buttons Row with Icons and Tooltips */}
                      <div className="pt-3 border-t">
                        {service.status === 'active' && (
                          <div className="flex items-center justify-center gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-10 w-10"
                                  onClick={() => {
                                    setSelectedService(service);
                                    setShowRenewDialog(true);
                                  }}
                                  data-testid={`renew-btn-${service.id}`}
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Renew Early</p>
                              </TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-10 w-10"
                                  onClick={() => handleOpenManageDialog(service)}
                                  data-testid={`manage-btn-${service.id}`}
                                >
                                  <Settings className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Manage Service</p>
                              </TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-10 w-10 text-destructive hover:bg-destructive hover:text-white"
                                  onClick={() => {
                                    setSelectedService(service);
                                    setShowCancelDialog(true);
                                  }}
                                  data-testid={`cancel-btn-${service.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Cancel Service</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        )}
                        
                        {service.status === 'cancelled' && (
                          <div className="text-sm text-muted-foreground text-center">
                            Service cancelled. Access continues until {formatDate(service.end_date)}
                          </div>
                        )}
                        
                        {service.status === 'expired' && (
                          <Link to="/services" className="block">
                            <Button variant="default" className="w-full">
                              Renew Service
                            </Button>
                          </Link>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-2">Need help with your services?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Check our knowledge base or contact support for assistance with billing, renewals, or technical issues.
                  </p>
                  <div className="flex gap-3">
                    <Link to="/knowledge-base">
                      <Button variant="outline">Knowledge Base</Button>
                    </Link>
                    <Link to="/support">
                      <Button>Contact Support</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Cancel Service Dialog */}
          <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Cancel Service</DialogTitle>
                <DialogDescription>
                  Are you sure you want to cancel &quot;{selectedService?.service_name}&quot;? Your service will remain active until {selectedService && formatDate(selectedService.end_date)}.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label>Please tell us why you&apos;re cancelling (required)</Label>
                <Textarea
                  placeholder="Your feedback helps us improve..."
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  rows={4}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCancelDialog(false)} disabled={processing}>
                  Keep Service
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleCancelService}
                  disabled={processing || !cancellationReason.trim()}
                >
                  {processing ? 'Cancelling...' : 'Cancel Service'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Renew Early Dialog */}
          <Dialog open={showRenewDialog} onOpenChange={setShowRenewDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Renew Early</DialogTitle>
                <DialogDescription>
                  Extend your &quot;{selectedService?.service_name}&quot; subscription before it expires.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Current Plan:</span>
                    <span className="font-medium">{selectedService?.pricing_tier_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Current Expiry:</span>
                    <span className="font-medium">{selectedService && formatDate(selectedService.end_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Days Remaining:</span>
                    <span className="font-semibold text-primary">
                      {selectedService && getDaysUntilRenewal(selectedService.end_date)} days
                    </span>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Renewing early will add time to your current subscription. 
                    Your new expiry date will be calculated from your current end date.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRenewDialog(false)}>
                  Cancel
                </Button>
                <Link to={`/services/${selectedService?.service_id}`}>
                  <Button onClick={() => setShowRenewDialog(false)}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Renew Now
                  </Button>
                </Link>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Enhanced Manage Service Dialog */}
          <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Manage Service</DialogTitle>
                <DialogDescription>
                  {selectedService?.service_name} - {selectedService?.pricing_tier_name}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Service Summary */}
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <Badge className={getStatusColor(selectedService?.status)}>
                      {selectedService?.status}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Renewal Date:</span>
                    <span className="font-medium">{selectedService && formatDate(selectedService.end_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Auto-renewal:</span>
                    <Badge variant={selectedService?.auto_renew ? 'default' : 'secondary'}>
                      {selectedService?.auto_renew ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="h-auto py-3 flex flex-col items-center gap-2"
                    onClick={() => {
                      setShowManageDialog(false);
                      navigate(`/services/${selectedService?.service_id}`);
                    }}
                  >
                    <ArrowUpCircle className="w-5 h-5 text-primary" />
                    <span className="text-sm">Upgrade Plan</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="h-auto py-3 flex flex-col items-center gap-2"
                    onClick={() => {
                      setShowManageDialog(false);
                      setShowRenewDialog(true);
                    }}
                  >
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                    <span className="text-sm">Renew Early</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="h-auto py-3 flex flex-col items-center gap-2"
                    onClick={() => handleDownloadReceipt(selectedService)}
                  >
                    <Download className="w-5 h-5 text-green-600" />
                    <span className="text-sm">Download Receipt</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="h-auto py-3 flex flex-col items-center gap-2"
                    onClick={() => {
                      setShowManageDialog(false);
                      navigate('/support');
                    }}
                  >
                    <MessageSquare className="w-5 h-5 text-orange-600" />
                    <span className="text-sm">Get Support</span>
                  </Button>
                </div>

                {/* Related Services */}
                {relatedServices.length > 0 && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center gap-2 mb-3">
                      <Plus className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Add Related Services</span>
                    </div>
                    <div className="space-y-2">
                      {relatedServices.map((rs) => (
                        <div
                          key={rs.id}
                          className="flex items-center justify-between p-2 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                          onClick={() => {
                            setShowManageDialog(false);
                            navigate(`/services/${rs.id}`);
                          }}
                        >
                          <div>
                            <p className="text-sm font-medium">{rs.name}</p>
                            <p className="text-xs text-muted-foreground">
                              From {formatPrice(rs.pricing_tiers?.[0]?.price || 0)}
                            </p>
                          </div>
                          <ArrowUpCircle className="w-4 h-4 text-primary" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Knowledge Base Link */}
                <div className="pt-4 border-t">
                  <Link 
                    to="/knowledge-base" 
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                    onClick={() => setShowManageDialog(false)}
                  >
                    <BookOpen className="w-4 h-4" />
                    Browse Knowledge Base for help articles
                  </Link>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowManageDialog(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </Layout>
    </TooltipProvider>
  );
}
